import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useUserRole } from "../../../context/UserRoleContext";
import { useArtist } from "../../../context/ArtistContext";
import DashboardLayout from "../../../components/common/DashboardLayout";
import ArtistSelector from "../../../components/common/ArtistSelector";
import MessageBell from "../../../components/common/MessageBell";
import NotificationBell from "../../../components/common/NotificationBell";
import useEarnings from "../hooks/useEarnings";
import ShowCardModal from "../components/ShowCardModal";
import { get } from "../../../services/api";
import { formatDateForInput } from "../../../utils/calendarDate";

const ArtistDashboard = () => {
  const { currentUser } = useAuth();
  const { userData, loading } = useUserRole();
  const { selectedArtist, shows, showsLoading, refreshShows, getShowsForDate } =
    useArtist();
  // Legacy support: use selectedArtist as artistProfile for compatibility
  const artistProfile = selectedArtist;
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [isShowModalOpen, setIsShowModalOpen] = useState(false);
  const [selectedShow, setSelectedShow] = useState(null);
  const [availabilityBlocks, setAvailabilityBlocks] = useState([]);
  const [blocksLoading, setBlocksLoading] = useState(false);

  // Calculate date ranges for the week view
  const weekDates = useMemo(() => {
    const dates = [];
    const today = new Date(currentDate);
    for (let i = -1; i < 6; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [currentDate]);

  const startDate = formatDateForInput(weekDates[0]);
  const endDate = formatDateForInput(weekDates[weekDates.length - 1]);

  const normalizedShows = useMemo(() => {
    if (!shows?.length) return [];
    return shows.map((show) => ({
      ...show,
      date: formatDateForInput(show.date),
    }));
  }, [shows]);

  const showsForWeek = useMemo(() => {
    if (!normalizedShows.length) return [];
    return normalizedShows.filter(
      (show) => show.date >= startDate && show.date <= endDate
    );
  }, [normalizedShows, startDate, endDate]);

  // Calculate date ranges for financial data
  const today = new Date();
  const todayDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  // Last 7 days: 7 days ago to today
  const last7DaysStart = new Date(todayDate);
  last7DaysStart.setDate(todayDate.getDate() - 6); // Include today, so 6 days ago

  // Last 30 days: 30 days ago to today
  const last30DaysStart = new Date(todayDate);
  last30DaysStart.setDate(todayDate.getDate() - 29); // Include today, so 29 days ago

  // Year to date: January 1st of current year to today
  const yearToDateStart = new Date(today.getFullYear(), 0, 1); // January 1st

  // Fetch earnings data for different periods
  const {
    totals: last7DaysTotals,
    loading: last7DaysLoading,
    refetch: refetchLast7Days,
  } = useEarnings({
    startDate: formatDateForInput(last7DaysStart),
    endDate: formatDateForInput(todayDate),
  });

  const {
    totals: last30DaysTotals,
    loading: last30DaysLoading,
    refetch: refetchLast30Days,
  } = useEarnings({
    startDate: formatDateForInput(last30DaysStart),
    endDate: formatDateForInput(todayDate),
  });

  const {
    totals: yearToDateTotals,
    loading: yearToDateLoading,
    refetch: refetchYearToDate,
  } = useEarnings({
    startDate: formatDateForInput(yearToDateStart),
    endDate: formatDateForInput(todayDate),
  });

  // Fetch availability blocks
  const fetchAvailabilityBlocks = useCallback(async () => {
    if (!artistProfile?.artist_id) return;

    try {
      setBlocksLoading(true);
      const data = await get("/availability/blocks");
      if (data.success) {
        setAvailabilityBlocks(data.blocks || []);
      }
    } catch (err) {
      console.error("Error fetching availability blocks:", err);
    } finally {
      setBlocksLoading(false);
    }
  }, [artistProfile?.artist_id]);

  // Fetch blocks when artist profile is available
  useEffect(() => {
    fetchAvailabilityBlocks();
  }, [fetchAvailabilityBlocks]);

  // Listen for shows updated event to refresh shows
  useEffect(() => {
    const handleShowsUpdated = () => {
      refreshShows();
    };
    window.addEventListener("showsUpdated", handleShowsUpdated);
    return () => {
      window.removeEventListener("showsUpdated", handleShowsUpdated);
    };
  }, [refreshShows]);

  // Check if a date is blocked
  const isDateBlocked = useCallback(
    (date) => {
      if (!availabilityBlocks.length) return null;

      const dateStr = formatDateForInput(date);

      for (const block of availabilityBlocks) {
        const blockStart = new Date(block.start_date);
        const blockEnd = new Date(block.end_date);
        const checkDate = new Date(dateStr);

        // Reset time to compare dates only
        blockStart.setHours(0, 0, 0, 0);
        blockEnd.setHours(23, 59, 59, 999);
        checkDate.setHours(0, 0, 0, 0);

        if (checkDate >= blockStart && checkDate <= blockEnd) {
          return {
            blocked: true,
            note: block.note || "Blocked Off",
          };
        }
      }

      return null;
    },
    [availabilityBlocks]
  );

  // Refresh all earnings data when a show's earnings are updated
  const handleEarningsUpdate = useCallback(() => {
    refetchLast7Days();
    refetchLast30Days();
    refetchYearToDate();
  }, [refetchLast7Days, refetchLast30Days, refetchYearToDate]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "pm" : "am";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes}${ampm}`;
  };

  const parseTimeToMinutes = (time) => {
    if (!time) return null;
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const minute = parseInt(minutes || "0", 10);
    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return null;
    }
    return hour * 60 + minute;
  };

  const getShowsForDay = (date) => {
    const dateStr = formatDateForInput(date);
    return showsForWeek
      .filter((show) => show.date === dateStr)
      .sort((a, b) => {
        const aStart = parseTimeToMinutes(a.start_time) ?? 0;
        const bStart = parseTimeToMinutes(b.start_time) ?? 0;
        return aStart - bStart;
      });
  };

  const handleShowClick = (show) => {
    setSelectedShow({
      ...show,
      date: show.date,
    });
    setIsShowModalOpen(true);
  };

  // Helper to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  // Helper to calculate artist earnings (base_pay + tips_cash + tips_electronic, excluding sales)
  const calculateArtistEarnings = (totals) => {
    if (!totals) return 0;
    return (
      (totals.total_base_pay || 0) +
      (totals.total_tips_cash || 0) +
      (totals.total_tips_electronic || 0)
    );
  };

  // Calendar navigation functions
  const moveDayBackward = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const moveDayForward = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const moveWeekBackward = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const moveWeekForward = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const isToday = (date) => {
    const todayDate = new Date();
    return (
      date.getDate() === todayDate.getDate() &&
      date.getMonth() === todayDate.getMonth() &&
      date.getFullYear() === todayDate.getFullYear()
    );
  };

  // Get month display for the week
  const getMonthDisplay = () => {
    const firstDate = weekDates[0];
    const lastDate = weekDates[weekDates.length - 1];
    const firstMonth = firstDate.toLocaleDateString("en-US", {
      month: "short",
    });
    const lastMonth = lastDate.toLocaleDateString("en-US", { month: "short" });

    if (firstMonth === lastMonth) {
      return firstMonth;
    }
    return `${firstMonth} - ${lastMonth}`;
  };

  const closeShowCardModal = () => {
    setIsShowModalOpen(false);
    setSelectedShow(null);
  };

  return (
    <DashboardLayout>
      <div className="artist-dashboard">
        {/* Calendar Section */}
        <div
          className="calendar-section"
          aria-busy={showsLoading ? "true" : "false"}
        >
          {showsLoading && (
            <div className="calendar-loading-overlay" role="status">
              <div className="calendar-loading-indicator">
                <span className="calendar-loading-spinner" aria-hidden="true" />
                <span className="calendar-loading-text">
                  Refreshing schedule…
                </span>
              </div>
            </div>
          )}
          {/* Calendar Navigation Buttons */}
          <div className="calendar-nav">
            <div className="calendar-nav-content">
              <div className="nav-btn-group">
                <button className="nav-btn week-btn" onClick={moveWeekBackward}>
                  ← Week
                </button>
                <button className="nav-btn day-btn" onClick={moveDayBackward}>
                  ← Day
                </button>
              </div>
              <div className="calendar-month-display">{getMonthDisplay()}</div>
              <div className="nav-btn-group">
                <button className="nav-btn day-btn" onClick={moveDayForward}>
                  Day →
                </button>
                <button className="nav-btn week-btn" onClick={moveWeekForward}>
                  Week →
                </button>
              </div>
            </div>
            <div className="calendar-nav-right">
              <MessageBell />
              <NotificationBell linkOnly />
            </div>
          </div>

          {/* Week Calendar */}
          <div className="week-calendar">
            {weekDates.map((date, index) => {
              const showsForDay = getShowsForDay(date);
              const blockInfo = isDateBlocked(date);
              return (
                <div
                  key={index}
                  className={`calendar-day ${isToday(date) ? "today" : ""} ${
                    blockInfo ? "blocked-day" : ""
                  }`}
                >
                  <div className="day-header">
                    <div className="day-name">
                      {date.toLocaleDateString("en-US", { weekday: "short" })}
                    </div>
                    <div className="day-number">{date.getDate()}</div>
                  </div>

                  <div className="day-shows">
                    {blockInfo ? (
                      <div className="blocked-day-message">
                        <span className="blocked-day-text">
                          {blockInfo.note}
                        </span>
                      </div>
                    ) : showsForDay.length === 0 ? (
                      <div className="no-shows-message">
                        <span className="no-show-text">No shows scheduled</span>
                      </div>
                    ) : (
                      showsForDay.map((show) => {
                        const showKey =
                          show.show_id ??
                          `${show.date}-${show.start_time}-${show.bar_id}`;
                        const isPending = show.status === "pending";
                        return (
                          <div
                            key={showKey}
                            className={`artist-show-card ${isPending ? "pending-show-card" : ""}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleShowClick(show)}
                            onKeyPress={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                handleShowClick(show);
                              }
                            }}
                          >
                            <div className="artist-show-card-header">
                              <span className="artist-show-card-time">
                                {formatTime(show.start_time)} -{" "}
                                {formatTime(show.end_time)}
                              </span>
                              {isPending && (
                                <span className="pending-badge">⏳ Pending</span>
                              )}
                            </div>
                            <div className="artist-show-card-content">
                              <span className="artist-show-card-stage-name">
                                {show.stage_name || "Artist"}
                              </span>
                              <span className="artist-show-card-venue">
                                {show.bar_name || "Venue"}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Financial Section */}
        <div className="financial-section">
          <h2 className="section-title">Earnings Overview</h2>
          <div className="financial-grid">
            <div className="financial-card">
              <span
                className="info-icon card-info-icon"
                data-tooltip="Your total earnings from all shows in the last 7 days (including today). Base pay, cash tips, and electronic tips from each show are added up for this period."
                aria-label="How this number is calculated"
              >
                ?
              </span>
              <h3>Last 7 Days</h3>
              <div className="earnings-breakdown">
                {last7DaysLoading ? (
                  <div className="time-slot">
                    <span className="slot-label">Loading...</span>
                    <span className="slot-amount">
                      <span className="spinner" />
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="time-slot">
                      <span className="slot-label">Base Pay</span>
                      <span className="slot-amount">
                        {formatCurrency(last7DaysTotals?.total_base_pay || 0)}
                      </span>
                    </div>
                    <div className="time-slot">
                      <span className="slot-label">Cash Tips</span>
                      <span className="slot-amount">
                        {formatCurrency(last7DaysTotals?.total_tips_cash || 0)}
                      </span>
                    </div>
                    <div className="time-slot">
                      <span className="slot-label">Electronic Tips</span>
                      <span className="slot-amount">
                        {formatCurrency(
                          last7DaysTotals?.total_tips_electronic || 0
                        )}
                      </span>
                    </div>
                    <div className="time-slot total">
                      <span className="slot-label">Total</span>
                      <span className="slot-amount">
                        {formatCurrency(
                          calculateArtistEarnings(last7DaysTotals)
                        )}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="financial-card">
              <span
                className="info-icon card-info-icon"
                data-tooltip="Your total earnings from all shows in the last 30 days (including today). Base pay, cash tips, and electronic tips from each show are added up for this period."
                aria-label="How this number is calculated"
              >
                ?
              </span>
              <h3>Last 30 Days</h3>
              <div className="earnings-breakdown">
                {last30DaysLoading ? (
                  <div className="time-slot">
                    <span className="slot-label">Loading...</span>
                    <span className="slot-amount">
                      <span className="spinner" />
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="time-slot">
                      <span className="slot-label">Base Pay</span>
                      <span className="slot-amount">
                        {formatCurrency(last30DaysTotals?.total_base_pay || 0)}
                      </span>
                    </div>
                    <div className="time-slot">
                      <span className="slot-label">Cash Tips</span>
                      <span className="slot-amount">
                        {formatCurrency(last30DaysTotals?.total_tips_cash || 0)}
                      </span>
                    </div>
                    <div className="time-slot">
                      <span className="slot-label">Electronic Tips</span>
                      <span className="slot-amount">
                        {formatCurrency(
                          last30DaysTotals?.total_tips_electronic || 0
                        )}
                      </span>
                    </div>
                    <div className="time-slot total">
                      <span className="slot-label">Total</span>
                      <span className="slot-amount">
                        {formatCurrency(
                          calculateArtistEarnings(last30DaysTotals)
                        )}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="financial-card">
              <span
                className="info-icon card-info-icon"
                data-tooltip="Your total earnings from January 1st of this year through today. Base pay, cash tips, and electronic tips from each show are added up for this period."
                aria-label="How this number is calculated"
              >
                ?
              </span>
              <h3>Year to Date</h3>
              <div className="earnings-breakdown">
                {yearToDateLoading ? (
                  <div className="time-slot">
                    <span className="slot-label">Loading...</span>
                    <span className="slot-amount">
                      <span className="spinner" />
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="time-slot">
                      <span className="slot-label">Base Pay</span>
                      <span className="slot-amount">
                        {formatCurrency(yearToDateTotals?.total_base_pay || 0)}
                      </span>
                    </div>
                    <div className="time-slot">
                      <span className="slot-label">Cash Tips</span>
                      <span className="slot-amount">
                        {formatCurrency(yearToDateTotals?.total_tips_cash || 0)}
                      </span>
                    </div>
                    <div className="time-slot">
                      <span className="slot-label">Electronic Tips</span>
                      <span className="slot-amount">
                        {formatCurrency(
                          yearToDateTotals?.total_tips_electronic || 0
                        )}
                      </span>
                    </div>
                    <div className="time-slot total">
                      <span className="slot-label">Total</span>
                      <span className="slot-amount">
                        {formatCurrency(
                          calculateArtistEarnings(yearToDateTotals)
                        )}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ShowCardModal
        isOpen={isShowModalOpen}
        onClose={closeShowCardModal}
        showId={selectedShow?.show_id}
        fallbackShow={selectedShow}
        onEarningsUpdate={handleEarningsUpdate}
      />
    </DashboardLayout>
  );
};

export default ArtistDashboard;
